import React, {
  useRef, Fragment, useEffect, useState
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { FormLayoutContent, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import ModalDialog from 'shared/modal/dialog';
import * as xtpActions from 'data/xtp/xtp.actions';
import * as xtpSelectors from 'data/xtp/xtp.selectors';
import * as xtpTypes from 'data/xtp/xtp.types';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import { AuditDetails, WithAuditDetails } from 'data/types';
import { Ref } from '../../components/XtpForm/XtpForm';
import {
  adaptTemplateVisibilityMode,
} from '../../utils/xtpTemplate.adapter';
import {
  getXtpAuditDetailsToSubmit,
} from '../../utils/xtpDataToSubmit';
import { xtpTemplate } from '../../templates';
import XtpForm from '../../components/XtpForm';
import ErrorsDialog from '../../components/ErrorsDialog';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  userId: state.authentication.userId,
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  deliveryFunctionId: state.global.selectedDF,
  xtps: xtpSelectors.getXtps(state),
  xtpOptions: xtpSelectors.getXtpOptions(state),
  xtpErrors: xtpSelectors.getXtpErrors(state),
  uiState: xtpSelectors.getUiState(state),
  formTemplate: adaptTemplateVisibilityMode(
    xtpTemplate,
    'view',
  ),
});

const mapDispatch = {
  getXtps: xtpActions.getXtps,
  goToXtpEdit: xtpActions.goToXtpEdit,
  deleteXtp: xtpActions.deleteXtp,
  getXtpOptions: xtpActions.getXtpOptions,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const XtpView = ({
  history,
  xtps,
  xtpOptions,
  deliveryFunctionId,
  formTemplate,
  isAuditEnabled,
  userId,
  userName,
  xtpErrors,
  uiState,
  getXtps,
  goToXtpEdit,
  deleteXtp,
  getXtpOptions,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [template, setTemplate] = useState(formTemplate);
  const [openConfirmActionDialog, setOpenConfirmActionDialog] = useState(false);
  const [formData, setFormData] = useState<Partial<{ xtp: xtpTypes.Xtp, xtpOptions: xtpTypes.XtpOptions}>>({});
  const formRef = useRef<Ref>(null);

  useEffect(() => {
    if (deliveryFunctionId == null) {
      return;
    }
    getXtps(deliveryFunctionId)
    getXtpOptions(deliveryFunctionId)
  }, [deliveryFunctionId, getXtps, getXtpOptions]);

  useEffect(() => {
    const clonedXtp: xtpTypes.Xtp = cloneDeep(xtps[0]);
    const cloneXtpOptions:xtpTypes.XtpOptions = cloneDeep(xtpOptions[0]);
    const newFormData:Partial<{ xtp: xtpTypes.Xtp, xtpOptions: xtpTypes.XtpOptions}> = {};
    const clonedTemplate = cloneDeep(formTemplate);
    if (clonedTemplate != null) {
      clonedTemplate.key = Date.now().toString();
    }

    if (clonedXtp != null) {
      newFormData.xtp = clonedXtp;
    }

    if (cloneXtpOptions != null) {
      newFormData.xtpOptions = cloneXtpOptions;
    }

    setFormData(newFormData);
    setTemplate(clonedTemplate);
  }, [xtps, xtpOptions, formTemplate]);

  const handleConfirmDelete = () => {
    const xtpId = xtps[0]?.id;
    if (xtpId == null) {
      return;
    }

    const body: WithAuditDetails<void> = { data: undefined };

    if (isAuditEnabled) {
      const auditDetails = getXtpAuditDetailsToSubmit(
        xtps[0],
        null,
        isAuditEnabled,
        userId,
        userName,
        'CREATE',
      );

      body.auditDetails = auditDetails as AuditDetails;
    }

    deleteXtp(deliveryFunctionId, xtpId, body);
  };

  const handleClickDelete = () => {
    setOpenConfirmActionDialog(true);
  };

  const handleClickCancelDelete = () => {
    setOpenConfirmActionDialog(false);
  };

  const handleClickEdit = () => {
    goToXtpEdit(history);
  };

  const renderForm = () => {
    if (!template || uiState.isLoading) {
      return null;
    }

    return (
      <div>
        <XtpForm
          ref={formRef}
          data={formData}
          template={template}
        />
      </div>
    );
  };

  const renderFormPanel = () => {
    return (
      <FormPanel title={t('xtp.view.title')}>
        <FormButtons
          isEditing={false}
          onClickEdit={handleClickEdit}
          onClickDelete={handleClickDelete}
        />
      </FormPanel>
    );
  };

  if (uiState.isLoading) {
    return <div />;
  }

  return (
    <div data-testid="XtpView">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel()}
          {renderForm()}
        </FormLayoutContent>
      </FormLayout>
      <ModalDialog
        width="500px"
        isOpen={openConfirmActionDialog}
        title={t('xtp.delete.title')}
        customComponent={() => (
          <Fragment>
            {t('xtp.delete.message')}
          </Fragment>
        )}
        actionText={t('xtp.delete.confirm')}
        onSubmit={handleConfirmDelete}
        onClose={handleClickCancelDelete}
      />
      <ErrorsDialog data={xtpErrors} wasXtpUpdatedWithErrors={uiState.wasXtpUpdatedWithErrors} />
    </div>
  );
};

export default withRouter(connector(XtpView));
